import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, Zap, Crown, Sparkles, Loader, AlertCircle } from 'lucide-react';
import { authService } from '../../../services/authService';
import { apiClient } from '../../../services/apiClient';

const PLAN_ICONS = [Zap, Crown, Sparkles];

const CmmsPlans = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [plansError, setPlansError] = useState(false);
  const [companyId, setCompanyId] = useState(null);
  const [annualDiscount, setAnnualDiscount] = useState(null);

  useEffect(() => {
    const user = authService.getCurrentUser();
    setCompanyId(user?.companyId ?? null);

    apiClient.get('/api/subscriptions/cmms/plans')
      .then(data => {
        const arr = Array.isArray(data) ? data : (data.content ?? []);
        setPlans(arr);
        const ref = arr.find(p => Number(p.monthlyPrice) > 0 && Number(p.annualPrice) > 0);
        if (ref) {
          const pct = Math.round((1 - Number(ref.annualPrice) / (Number(ref.monthlyPrice) * 12)) * 100);
          setAnnualDiscount(pct > 0 ? pct : null);
        }
      })
      .catch(() => setPlansError(true))
      .finally(() => setLoadingPlans(false));
  }, []);

  const handleSubscribe = (plan) => {
    navigate('/portal/checkout', {
      state: { plan, billingCycle, serviceType: 'CMMS' },
    });
  };

  const getIncludedFeatures = (plan) => {
    const extras = [
      plan.includedUsers != null ? t('cmms.plns_upToUsers', { count: plan.includedUsers }) : null,
      plan.includedAssets != null ? t('cmms.plns_upToAssets', { count: plan.includedAssets }) : null,
      plan.includedLocations != null ? t('cmms.plns_upToLocations', { count: plan.includedLocations }) : null,
      plan.includedBranches != null ? t('cmms.plns_upToBranches', { count: plan.includedBranches }) : null,
    ].filter(Boolean);
    const apiFeatures = Array.isArray(plan.features) ? plan.features : [];
    return [...extras, ...apiFeatures];
  };

  if (loadingPlans) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (plansError) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-center gap-3">
          <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
          <div>
            <p className="font-semibold text-red-900">{t('cmms.plns_failed')}</p>
            <p className="text-sm text-red-700 mt-1">{t('cmms.plns_failedDesc')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {t('cmms.plns_title')}
        </h1>
        <p className="text-gray-600">
          {t('cmms.plns_subtitle')}
        </p>
      </div>

      {/* Billing Toggle */}
      <div className="flex justify-center mb-12">
        <div className="bg-gray-100 p-1 rounded-lg inline-flex">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-6 py-2 rounded-md font-medium transition-all ${
              billingCycle === 'monthly'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('billing.monthly')}
          </button>
          <button
            onClick={() => setBillingCycle('annual')}
            className={`px-6 py-2 rounded-md font-medium transition-all ${
              billingCycle === 'annual'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('billing.annual')}
            {annualDiscount != null && (
              <span className="ms-4 text-xs text-green-600 font-semibold">{t('billing.saveDiscount', { pct: annualDiscount })}</span>
            )}
          </button>
        </div>
      </div>

      {/* Plans */}
      <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
        {plans.map((plan, index) => {
          const Icon = PLAN_ICONS[index] ?? Sparkles;
          const highlighted = plan.popular || index === 1;
          const price = billingCycle === 'monthly'
            ? Number(plan.monthlyPrice) || 0
            : Number(plan.annualPrice) || 0;
          const priceLabel = billingCycle === 'monthly' ? '/month' : '/year';
          const features = getIncludedFeatures(plan);
          return (
            <div
              key={plan.id}
              className={`relative bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-2xl ${
                highlighted ? 'border-2 border-primary-500 transform scale-105' : 'border border-gray-200'
              }`}
            >
              {highlighted && (
                <div className="absolute top-0 right-0">
                  <div className="bg-primary-500 text-white px-4 py-1 text-xs font-bold rounded-bl-lg">
                    {t('cmms.plns_mostPopular')}
                  </div>
                </div>
              )}

              <div className="p-8">
                {/* Icon & Name */}
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    highlighted
                      ? 'bg-gradient-to-br from-primary-500 to-blue-600'
                      : 'bg-gradient-to-br from-gray-400 to-gray-600'
                  }`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {plan.planName || plan.name}
                  </h3>
                </div>

                <p className="text-gray-600 mb-6">{plan.description}</p>

                {/* Price */}
                <div className="mb-6">
                  {price === 0 ? (
                    <div className="text-3xl font-bold text-gray-900">{t('cmms.plns_custom')}</div>
                  ) : (
                    <>
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-bold text-gray-900">${price}</span>
                        <span className="text-gray-600">{billingCycle === 'monthly' ? t('billing.perMonth') : t('billing.perYear')}</span>
                      </div>
                      {billingCycle === 'annual' && (
                        <p className="text-sm text-gray-500 mt-1">
                          {t('cmms.plns_billedAnnually', { price: (price / 12).toFixed(2) })}
                        </p>
                      )}
                    </>
                  )}
                </div>

                {/* Subscribe Button */}
                <button
                  onClick={() => handleSubscribe(plan)}
                  className={`w-full py-3 px-6 rounded-lg font-semibold transition-all mb-6 ${
                    highlighted
                      ? 'bg-primary-600 text-white hover:bg-primary-700 shadow-lg hover:shadow-xl'
                      : 'bg-gray-900 text-white hover:bg-gray-800'
                  }`}
                >
                  {t('dashboard.subscribeNow')}
                </button>

                {/* Features */}
                {features.length > 0 && (
                  <div className="space-y-3">
                    <p className="text-sm font-semibold text-gray-900 mb-3">{t('cmms.plns_whatsIncluded')}</p>
                    {features.map((feature, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                )}

              </div>
            </div>
          );
        })}
      </div>

      {/* FAQ or Additional Info */}
      <div className="mt-16 max-w-4xl mx-auto">
        <div className="bg-gray-50 rounded-xl p-8 text-center">
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            {t('cmms.plns_customTitle')}
          </h3>
          <p className="text-gray-600 mb-4">
            {t('cmms.plns_customDesc')}
          </p>
          <button
            onClick={() => navigate('/portal/support')}
            className="btn btn-outline"
          >
            {t('cmms.plns_contactSales')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CmmsPlans;
